import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Search, UserPlus, Cpu } from "lucide-react";
import api from "@/lib/api";
import { ExpertCard } from "@/components/ExpertCard";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export default function Experts() {
  const { user } = useAuth();
  const [experts, setExperts] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSpec, setActiveSpec] = useState("");
  const [endorsedOnly, setEndorsedOnly] = useState(false);
  const [search, setSearch] = useState("");

  const searchRef = useRef(search);
  searchRef.current = search;
  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (activeSpec) params.specialization = activeSpec;
    if (endorsedOnly) params.endorsed = true;
    if (searchRef.current.trim()) params.search = searchRef.current.trim();
    api.get("/experts", { params }).then((r) => setExperts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [activeSpec, endorsedOnly]);

  useEffect(() => { api.get("/experts/specializations").then((r) => setSpecs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-slate-950 text-white min-h-screen" data-testid="experts-page">
      <div className="border-b border-slate-800 bg-[radial-gradient(circle_at_20%_-10%,rgba(234,88,12,0.15),transparent_45%)]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">Capital uman · Engineering Guild</span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mt-4 max-w-2xl">Directorul de experți B2B</h1>
          <blockquote className="border-l-2 border-orange-600 pl-4 mt-5 max-w-2xl" data-testid="proverb-quote">
            <p className="text-lg sm:text-xl font-bold text-white italic">„Cine nu are oameni experimentați, să-și cumpere.”</p>
          </blockquote>
          <p className="text-slate-400 mt-4 max-w-xl leading-relaxed">În industria grea a lemnului și biomasei, un utilaj este la fel de eficient ca mintea care îl calibrează. Evită opririle costisitoare — angajează expertiză dovedită. Fiecare colaborare este o <span className="text-slate-200 font-semibold">poliță de asigurare</span> împotriva eșecului operațional.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Caută după nume, specializare, certificare..." className="rounded-none bg-slate-900 border-slate-700 text-white pl-10" data-testid="expert-search" />
            </div>
            <Link to={user ? "/cont-expert" : "/autentificare"} className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors shrink-0" data-testid="become-expert-cta">
              <UserPlus className="w-4 h-4" /> Devino expert
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <button onClick={() => setActiveSpec("")} className={`px-4 py-2 text-xs uppercase tracking-wide font-bold border transition-colors ${!activeSpec ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`} data-testid="spec-filter-all">Toate</button>
          {specs.map((s) => (
            <button key={s} onClick={() => setActiveSpec(s)} className={`px-4 py-2 text-xs uppercase tracking-wide font-bold border transition-colors ${activeSpec === s ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`} data-testid={`spec-filter-${s}`}>{s}</button>
          ))}
          <button onClick={() => setEndorsedOnly((v) => !v)} className={`ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wide font-bold border transition-colors ${endorsedOnly ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`} data-testid="endorsed-filter">
            <BadgeCheck className="w-4 h-4" /> Doar OXO Endorsed
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] bg-slate-800 rounded-none" />)
          ) : experts.length === 0 ? (
            <div className="col-span-full border border-slate-800 p-16 text-center" data-testid="no-experts">
              <Cpu className="w-10 h-10 mx-auto text-slate-600" />
              <p className="text-slate-400 mt-4">Niciun expert în această categorie încă.</p>
              <Link to={user ? "/cont-expert" : "/autentificare"} className="inline-block mt-4 text-orange-500 font-semibold hover:underline">Înscrie-te ca primul specialist</Link>
            </div>
          ) : (
            experts.map((e) => <ExpertCard key={e.id} expert={e} />)
          )}
        </div>
      </div>
    </div>
  );
}
